import { useMemo } from 'react';
import { AllocationsOverTimeChart } from '../../components/molecules/AllocationsOverTimeChart';
import useMarketPortfolioData, {
    MarketPortfolioTenure
} from '../../components/organisms/PortfolioComposition/useMarketPortfolioData';
import { UniversalCard } from '../../components/molecules';
import { useTranslation } from 'react-i18next';

const getAssetArrayFromPortfolios = (portfolios: Array<any> = [], portfolioPath: Array<number>, assetType: string) =>
    portfolioPath.map((yearPortfolioId) => {
        const { assets } = portfolios.find((p: any) => p.portfolioId === yearPortfolioId) || { assets: [] };
        return assets.find((asset: any) => asset.type === assetType)?.totalAllocatedvalue || 0;
    });

export const AllocationsOverTimeTab = ({
    portfolioPath,
    retirementYear,
    scenarioType,
    isPDF,
    hideCard = false,
    tenure
}: {
    portfolioPath: Array<number>;
    retirementYear?: number;
    scenarioType?: string;
    isPDF?: boolean;
    hideCard?: boolean;
    tenure?: MarketPortfolioTenure;
}) => {
    const { t } = useTranslation();
    const { allocationOverTimeSource } = useMarketPortfolioData(tenure);

    const generateEquityArray = useMemo(
        () => getAssetArrayFromPortfolios(allocationOverTimeSource, portfolioPath, 'Equity'),
        [allocationOverTimeSource, portfolioPath]
    );

    const generateFixedIncomeArray = useMemo(
        () => getAssetArrayFromPortfolios(allocationOverTimeSource, portfolioPath, 'Fixed Income'),
        [allocationOverTimeSource, portfolioPath]
    );

    const generateAlternativesArray = useMemo(
        () => getAssetArrayFromPortfolios(allocationOverTimeSource, portfolioPath, 'Alternatives'),
        [allocationOverTimeSource, portfolioPath]
    );

    const generateCashArray = useMemo(
        () => getAssetArrayFromPortfolios(allocationOverTimeSource, portfolioPath, 'Cash'),
        [allocationOverTimeSource, portfolioPath]
    );

    // Helper to get last element or 0 if array is empty
    const lastOrZero = (arr: number[]) => (arr && arr.length ? arr[arr.length - 1] : 0);

    const chartContent = (
        <>
            <AllocationsOverTimeChart
                startingYear={new Date().getFullYear()} // current year
                equity={[...generateEquityArray, lastOrZero(generateEquityArray)]}
                fixedIncome={[...generateFixedIncomeArray, lastOrZero(generateFixedIncomeArray)]}
                alternatives={[...generateAlternativesArray, lastOrZero(generateAlternativesArray)]}
                cash={[...generateCashArray, lastOrZero(generateCashArray)]}
                retirementYear={retirementYear}
                isReadOnly={isPDF}
                scenarioType={scenarioType}
            />
        </>
    );

    if (hideCard || isPDF) {
        return chartContent;
    }

    return <UniversalCard header={t('LABEL_ALLOCATION_OVER_TIME')}>{chartContent}</UniversalCard>;
};
